/**
 * EKS cart drawer. Custom implementation, but contract-compatible with the
 * Dawn-derived scripts already in this theme:
 *  - product-form.js finds document.querySelector('cart-drawer') and calls
 *    getSectionsToRender() / setActiveElement() / renderContents(state)
 *    instead of redirecting to /cart after an add.
 *  - cart.js's CartItems drives quantity/remove; CartDrawerItems below
 *    subclasses it with drawer-scoped sections so in-drawer +/- and trash
 *    re-render the drawer, footer and header bubble.
 * Load order matters: cart.js must be loaded before this file (the drawer
 * section includes it on non-cart pages; the cart page already loads it).
 */

if (!customElements.get('cart-drawer')) {
  customElements.define(
    'cart-drawer',
    class CartDrawer extends HTMLElement {
      constructor() {
        super();
        this.addEventListener('keyup', (e) => {
          if (e.code === 'Escape') this.close();
        });
        const overlay = this.querySelector('#CartDrawer-Overlay');
        if (overlay) overlay.addEventListener('click', () => this.close());
        this.querySelectorAll('[data-drawer-close]').forEach((btn) =>
          btn.addEventListener('click', () => this.close())
        );
        this.bindCartIcon();
      }

      bindCartIcon() {
        const icon = document.getElementById('cart-icon-bubble');
        if (!icon || document.body.classList.contains('template-cart')) return;
        icon.setAttribute('aria-haspopup', 'dialog');
        icon.addEventListener('click', (e) => {
          e.preventDefault();
          this.open(icon);
        });
      }

      open(opener) {
        if (opener) this.setActiveElement(opener);
        this.classList.add('active');
        document.body.classList.add('eks-cdrawer-open');
        setTimeout(() => {
          const panel = this.querySelector('#CartDrawer');
          if (typeof trapFocus === 'function' && panel) {
            try { trapFocus(panel); } catch (e) { /* focus trap optional */ }
          } else if (panel) {
            panel.focus();
          }
        });
      }

      close() {
        this.classList.remove('active');
        document.body.classList.remove('eks-cdrawer-open');
        if (typeof removeTrapFocus === 'function') {
          try { removeTrapFocus(this.activeElement); } catch (e) { /* optional */ }
        }
      }

      setActiveElement(element) {
        this.activeElement = element;
      }

      /* product-form.js maps .id into the add-to-cart `sections` param. */
      getSectionsToRender() {
        return [{ id: 'cart-drawer' }, { id: 'cart-icon-bubble' }];
      }

      renderContents(parsedState) {
        const sections = parsedState.sections || {};
        if (sections['cart-drawer']) {
          const html = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
          ['cart-drawer-items', '.cart-drawer__footer', '.eks-cdrawer__count'].forEach((selector) => {
            const src = html.querySelector(selector);
            const dst = this.querySelector(selector);
            if (src && dst) {
              dst.innerHTML = src.innerHTML;
              if (src.className) dst.className = src.className;
            }
          });
        }
        if (sections['cart-icon-bubble']) {
          const bubble = document.getElementById('cart-icon-bubble');
          if (bubble) {
            const html = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
            const src = html.querySelector('.shopify-section') || html.body;
            bubble.innerHTML = src.innerHTML;
          }
        }
        this.classList.remove('is-empty');
        this.open();
      }
    }
  );
}

/* Quantity/remove inside the drawer: reuse CartItems from cart.js with
   drawer-scoped section rendering. */
if (customElements.get('cart-items') && !customElements.get('cart-drawer-items')) {
  customElements.define(
    'cart-drawer-items',
    class CartDrawerItems extends (customElements.get('cart-items')) {
      getSectionsToRender() {
        return [
          {
            id: 'CartDrawer-CartItems',
            section: 'cart-drawer',
            selector: 'cart-drawer-items',
          },
          {
            id: 'CartDrawer-Footer',
            section: 'cart-drawer',
            selector: '.cart-drawer__footer',
          },
          {
            id: 'cart-icon-bubble',
            section: 'cart-icon-bubble',
            selector: '.shopify-section',
          },
        ];
      }
    }
  );
}
